import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AdminLayout from "../components/AdminLayout";
import { getPayoutSummary, listPayoutEntries, settlePayouts, PayoutStatus, type PayoutSummary } from "@/lib/admin";

const money = (n: number) => `$${n.toLocaleString("es-MX")}`;

export default function AdminComisionesPage() {
  const { t } = useTranslation("admin");
  const [rows, setRows] = useState<PayoutSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [settlingId, setSettlingId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    getPayoutSummary()
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalPending = rows.reduce((s, r) => s + r.pendingNet, 0);
  const totalSettled = rows.reduce((s, r) => s + r.settledNet, 0);

  const handleSettle = async (row: PayoutSummary) => {
    if (row.pendingCount === 0) return;
    const reference = window.prompt(
      `${row.providerName} — ${money(row.pendingNet)}\n${t("admin.com.settle")}:`
    );
    if (!reference?.trim()) return;
    setSettlingId(row.providerId);
    try {
      const pending = await listPayoutEntries({ providerId: row.providerId, status: PayoutStatus.Pending });
      if (pending.length > 0) {
        await settlePayouts(pending.map((p) => p.id), reference.trim());
      }
      load();
    } catch {
      /* surfaced by refresh */
    } finally {
      setSettlingId(null);
    }
  };

  return (
    <AdminLayout title={t("admin.com.title")}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[
          { label: t("admin.com.pending"), value: money(totalPending), icon: "ri-time-line", color: "bg-sand/60 text-charcoal" },
          { label: t("admin.com.settledTotal"), value: money(totalSettled), icon: "ri-checkbox-circle-line", color: "bg-ocean/10 text-ocean" },
          { label: t("admin.com.providers"), value: String(rows.length), icon: "ri-store-2-line", color: "bg-coral/10 text-coral" },
        ].map((kpi) => (
          <div key={kpi.label} className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${kpi.color} mb-3`}>
              <i className={`${kpi.icon} text-lg`} />
            </div>
            <p className="text-2xl font-bold text-charcoal">{kpi.value}</p>
            <p className="text-xs text-gray-500 mt-1">{kpi.label}</p>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left text-xs font-medium text-gray-400 py-3 pr-4">{t("admin.com.provider")}</th>
              <th className="text-left text-xs font-medium text-gray-400 py-3 pr-4">{t("admin.com.pendingBookings")}</th>
              <th className="text-left text-xs font-medium text-gray-400 py-3 pr-4">{t("admin.com.pendingNet")}</th>
              <th className="text-left text-xs font-medium text-gray-400 py-3 pr-4">{t("admin.com.settledNet")}</th>
              <th className="text-left text-xs font-medium text-gray-400 py-3">{t("admin.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="py-10 text-center text-gray-400">{t("admin.loading")}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} className="py-10 text-center text-gray-400">{t("admin.com.empty")}</td></tr>
            ) : (
              rows.map((r) => (
                <tr key={r.providerId} className="border-b border-gray-50 last:border-0">
                  <td className="py-3 pr-4 font-medium text-charcoal">{r.providerName}</td>
                  <td className="py-3 pr-4 text-gray-600">{r.pendingCount}</td>
                  <td className="py-3 pr-4 font-medium text-charcoal">{money(r.pendingNet)}</td>
                  <td className="py-3 pr-4 text-gray-500">{money(r.settledNet)}</td>
                  <td className="py-3">
                    {r.pendingCount > 0 ? (
                      <button
                        onClick={() => handleSettle(r)}
                        disabled={settlingId === r.providerId}
                        className="text-xs font-medium px-3 py-1.5 rounded-full bg-ocean text-white hover:bg-ocean/90 disabled:opacity-50"
                      >
                        {settlingId === r.providerId ? t("admin.com.settling") : t("admin.com.settle")}
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">{t("admin.com.upToDate")}</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
